/**
 * Lets a widget inside a Radix layer (a modal, a menu, a panel) keep Escape for itself.
 *
 * Radix dismisses its top layer from a capturing listener on the document, before the
 * focused element sees the key, so Escape in a date field's calendar would close the
 * modal or panel around it instead of the calendar. Pass this as the layer's
 * `onEscapeKeyDown`: it holds the dismissal back and closes the layer only if the key
 * bubbles out of the app untouched, which an inner widget prevents by stopping it. A key
 * that closes the layer goes no further, so a page-level Escape handler, such as the one
 * that closes the mobile sidebar, does not act on it as well.
 */
export function deferEscapeKey(event: KeyboardEvent, close: () => void): void {
    event.preventDefault();
    const doc = event.target instanceof Node ? (event.target.ownerDocument ?? document) : document;
    // React handles keys at its root and portal containers, which sit inside the body, so
    // the body sees the key after every component handler and before the document.
    const container: Node = doc.body ?? doc;

    function onBubble(bubbled: Event) {
        if (bubbled !== event) return;
        container.removeEventListener('keydown', onBubble);
        // A portal's handlers run on the body too, before this listener; a stop there does
        // not skip listeners on the same node, but it does mark the event.
        if (bubbled.cancelBubble) return;
        bubbled.stopPropagation();
        close();
    }

    container.addEventListener('keydown', onBubble);
    // A stopped key never arrives; this drops the listener once the event is done.
    window.setTimeout(() => container.removeEventListener('keydown', onBubble), 0);
}
